import Link from '@/lib/siteLink';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Globe, Smartphone, Server, Gamepad2, Code2, Shield, Zap, Users } from 'lucide-react';
import DevmobNav from './DevmobNav';
import DevmobFooter from './DevmobFooter';
import { SERVICES, CLIENTS } from './devmobData';

export default function DevmobHome() {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>DevMob, Technology Services | Web, Mobile, Enterprise, Gaming</title>
        <meta name="description" content="DevMob is a Bangalore-based technology services company that builds websites, mobile apps, enterprise software, and gaming products. 19+ products delivered for clients across India, MENA, Europe, and North America." />
        <meta name="keywords" content="web development agency, mobile app development, react native developers, enterprise software, gaming app development, Bangalore software company, custom software services" />
        <link rel="canonical" href="https://devmob.co" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="DevMob" />
        <meta property="og:title" content="DevMob, Technology Services" />
        <meta property="og:description" content="Websites, mobile apps, enterprise software, and gaming products, built end-to-end by DevMob." />
        <meta property="og:url" content="https://devmob.co/" />
        <meta property="og:image" content="https://devmob.co/og-devmob.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="DevMob, Technology Services" />
        <meta name="twitter:description" content="Websites, mobile apps, enterprise software, and gaming products, built end-to-end by DevMob." />
        <meta name="twitter:image" content="https://devmob.co/og-devmob.jpg" />

        {/* AEO/GEO: Organization */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org", "@type": "Organization",
          "name": "DevMob", "url": "https://devmob.co",
          "logo": "https://devmob.co/devmob-logo.png",
          "description": "DevMob is a technology services company building websites, mobile apps, enterprise software, and gaming products for businesses worldwide.",
          "email": "project@devmob.co",
          "address": { "@type": "PostalAddress", "addressLocality": "Bangalore", "addressRegion": "Karnataka", "addressCountry": "IN" },
          "areaServed": ["India", "United Arab Emirates", "United Kingdom", "Germany", "Turkey", "United States"],
          "knowsAbout": ["Web Development", "Mobile App Development", "Enterprise Software", "Game Development", "React", "React Native", "Node.js", "Python"]
        })}</script>

        {/* AEO: ProfessionalService */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org", "@type": "ProfessionalService",
          "name": "DevMob",
          "image": "https://devmob.co/og-devmob.jpg",
          "url": "https://devmob.co",
          "priceRange": "₹₹",
          "address": { "@type": "PostalAddress", "addressLocality": "Bangalore", "addressCountry": "IN" },
          "serviceType": ["Web Development", "Mobile App Development", "Enterprise Software", "Gaming Apps"]
        })}</script>

        {/* AEO: FAQPage, surfaces in Google AI Overview / ChatGPT search */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org", "@type": "FAQPage",
          "mainEntity": [
            { "@type": "Question", "name": "What does DevMob build?", "acceptedAnswer": { "@type": "Answer", "text": "DevMob builds websites, mobile applications (iOS and Android), enterprise software (ERP, CRM, internal tools), and gaming products including casual games, esports platforms, and real-time multiplayer experiences." } },
            { "@type": "Question", "name": "Where is DevMob based?", "acceptedAnswer": { "@type": "Answer", "text": "DevMob is headquartered in Bangalore, India, and delivers projects to clients across India, MENA, Europe (UK, Germany, Turkey), Southeast Asia, and North America." } },
            { "@type": "Question", "name": "How can I start a project with DevMob?", "acceptedAnswer": { "@type": "Answer", "text": "Email project@devmob.co or submit a brief at https://devmob.co/contact — the team reviews and responds within 24 hours." } },
            { "@type": "Question", "name": "What technologies does DevMob use?", "acceptedAnswer": { "@type": "Answer", "text": "React, Next.js, Node.js, Python, FastAPI for web. React Native, Flutter, Swift, Kotlin for mobile. Unity and Unreal Engine for gaming. AWS and Vercel for cloud." } }
          ]
        })}</script>
      </Helmet>

      <DevmobNav />

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="devmob-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B1120] via-[#0F1A2E] to-[#0B1120]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(43,125,233,0.12),transparent_60%)]" />
        <div className="max-w-6xl mx-auto px-6 py-24 md:py-32 relative">
          <p className="text-[#4A9FF5] text-sm font-semibold tracking-wide mb-4">Technology Services</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-[1.08] max-w-3xl">
            We build software that moves businesses forward
          </h1>
          <p className="mt-6 text-lg text-gray-400 max-w-2xl leading-relaxed">
            From websites and mobile apps to enterprise platforms and gaming products, DevMob is your end-to-end technology partner.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/devmob/estimate" className="inline-flex items-center gap-2 bg-[#2B7DE9] text-white px-7 py-3.5 rounded-lg font-bold text-sm hover:bg-[#1A6AD4] transition-all" data-testid="devmob-hero-cta">
              Get a Free Estimate <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/devmob/portfolio" className="inline-flex items-center gap-2 border border-white/15 text-white px-7 py-3.5 rounded-lg font-semibold text-sm hover:bg-white/5 transition-all">
              View Our Work
            </Link>
          </div>
          <p className="text-xs text-gray-500 mt-4">Free · 2 minutes · Time, team, tech & cost, instantly</p>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 bg-white" data-testid="devmob-services">
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">What We Build</p>
          <h2 className="text-2xl sm:text-3xl font-black text-gray-900 mb-10">Full-spectrum technology services</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {SERVICES.map((s) => (
              <Link key={s.slug} to="/devmob/services" className="group border border-gray-200 rounded-2xl p-6 hover:shadow-xl hover:border-[#2B7DE9]/30 transition-all" data-testid={`home-svc-${s.slug}`}>
                <div className="w-10 h-10 rounded-xl bg-[#2B7DE9]/10 flex items-center justify-center mb-4">
                  <s.icon className="w-5 h-5 text-[#2B7DE9]" />
                </div>
                <h3 className="font-bold text-gray-900 mb-1 group-hover:text-[#2B7DE9] transition-colors">{s.title}</h3>
                <p className="text-sm text-gray-500 line-clamp-2">{s.short}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why DevMob */}
      <section className="py-20 bg-gray-50" data-testid="devmob-why">
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Why DevMob</p>
          <h2 className="text-2xl sm:text-3xl font-black text-gray-900 mb-10">Built for businesses that ship</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Zap, title: 'Speed to Market', desc: 'Agile sprints, rapid prototyping, and CI/CD pipelines get your product live faster.' },
              { icon: Shield, title: 'Enterprise-Grade Quality', desc: 'Clean architecture, automated testing, security-first design. Built to scale.' },
              { icon: Users, title: 'Dedicated Teams', desc: 'Full-stack squads assigned to your project, designers, devs, QA, project leads.' },
            ].map((d) => (
              <div key={d.title} className="bg-white border border-gray-200 rounded-2xl p-7">
                <d.icon className="w-6 h-6 text-[#2B7DE9] mb-4" />
                <h3 className="font-bold text-gray-900 mb-2">{d.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Clients Marquee */}
      <section className="py-14 bg-white border-y border-gray-100" data-testid="devmob-clients">
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-6">Trusted By</p>
          <div className="flex flex-wrap gap-3">
            {CLIENTS.map((c) => (
              <span key={c.name} className="px-5 py-2.5 bg-gray-50 border border-gray-200 rounded-full text-sm font-semibold text-gray-700">{c.name}</span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0B1120] text-center">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="text-2xl sm:text-3xl font-black text-white mb-4">Have a project in mind?</h2>
          <p className="text-gray-400 mb-8 text-sm">Tell us about it. We'll get back to you within 24 hours with a plan.</p>
          <Link to="/devmob/contact" className="inline-flex items-center gap-2 bg-[#2B7DE9] text-white px-8 py-4 rounded-lg font-bold text-sm hover:bg-[#1A6AD4] transition-all" data-testid="devmob-cta-bottom">
            Start a Project <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <DevmobFooter />
    </div>
  );
}
